'use client'

import { ExpandMoreIcon } from '@repo/ui/ExpandMoreIcon'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { FolderIcon } from '@repo/ui/FolderIcon'
import { useEffect, useRef, useState } from 'react'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { useTheme } from '@mui/material/styles'
import MUIFormControl from '@mui/material/FormControl'
import MUIIconButton from '@mui/material/IconButton'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'
import MUITypography from '@mui/material/Typography'
import type { SelectChangeEvent } from '@mui/material/Select'

const LABEL_ID = 'form-evo-category-label'

type FormEvoCategory = {
  formCategoryId: number
  formProviderId: string
  parentFormCategoryId: number
  name: string
  description: string
  createdDate: string
  updatedDate: string
  formCategories: FormEvoCategory[]
}

type FormEvoCategorySelectProps = {
  onChange?: (value: number | null) => void
  sx?: object
}

const getCurrentLevel = (tree: FormEvoCategory[], parentHistory: (number | null)[]): FormEvoCategory[] => {
  let level = tree ?? []
  for (const id of parentHistory) {
    if (id === null) continue
    const node = level.find((n) => n.formCategoryId === id)
    if (!node) return []
    level = node.formCategories ?? []
  }
  return level
}

export const FormEvoCategorySelect = ({ onChange = () => {}, sx }: FormEvoCategorySelectProps) => {
  const [open, setOpen] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [parentHistory, setParentHistory] = useState<(number | null)[]>([null])
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined)
  const formControlRef = useRef<HTMLDivElement>(null)

  const {
    getAllNestedFormEvoCategories,
    formEvoCategories,
    selectedFormEvoCategory,
    setSelectedFormEvoCategory
  } = useThirdPartyFormsStore()

  const theme = useTheme()

  useEffect(() => {
    void getAllNestedFormEvoCategories()
  }, [getAllNestedFormEvoCategories])

  const currentCategories = getCurrentLevel(formEvoCategories ?? [], parentHistory)
  const selectedId: number | null = selectedFormEvoCategory?.formCategoryId ?? null
  const selectedName: string | null = selectedFormEvoCategory?.name ?? null

  const handleSelectChange = (_e: SelectChangeEvent<number | string>) => {
    // no-op on purpose. used to suppress MUI warning
  }

  const handleOpen = () => {
    setShouldClose(false)
    setParentHistory([null])
    if (formControlRef.current) {
      setMenuWidth(formControlRef.current.clientWidth)
    }
    void getAllNestedFormEvoCategories()
    setOpen(true)
  }

  const handleClose = () => {
    if (shouldClose) {
      setOpen(false)
      setShouldClose(false)
    }
  }

  const handleLeafSelect = (category: FormEvoCategory) => {
    setSelectedFormEvoCategory(category)
    onChange(category.formCategoryId)
    setShouldClose(true)
    setOpen(false)
  }

  const handleCategoryClick = (category: FormEvoCategory) => {
    const hasChildren = (category.formCategories?.length ?? 0) > 0

    if (!hasChildren) {
      handleLeafSelect(category)
    } else {
      setParentHistory((prev) => [...prev, category.formCategoryId])
    }
  }

  const handleBack = () => {
    if (parentHistory.length <= 1) return
    setParentHistory((prev) => prev.slice(0, -1))
  }

  const handleClear = () => {
    setShouldClose(false)
    setSelectedFormEvoCategory(null)
    onChange(null)
  }

  return (
    <MUIFormControl ref={formControlRef} sx={{ width: '100%', ...sx }} variant="outlined">
      <MUIInputLabel id={LABEL_ID}>FormEvo Category</MUIInputLabel>
      <MUISelect
        labelId={LABEL_ID}
        label="FormEvo Category"
        multiple={false}
        value={selectedId ?? ''}
        onChange={handleSelectChange}
        sx={{ width: '100%' }}
        open={open}
        onOpen={() => handleOpen()}
        onClose={handleClose}
        renderValue={() => selectedName || ''}
        MenuProps={{
          disableAutoFocus: true,
          PaperProps: {
            style: menuWidth ? { width: menuWidth } : undefined
          },
          onClose: (_event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              setOpen(false)
              setParentHistory([null])
            }
          }
        }}
        endAdornment={
          selectedId != null ? (
            <MUIInputAdornment sx={{ marginRight: '10px' }} position="end">
              <MUIIconButton onClick={handleClear}>
                <CloseIcon sx={{ width: '0.7rem', height: '0.7rem', mr: 1 }} />
              </MUIIconButton>
            </MUIInputAdornment>
          ) : null
        }
      >
        {selectedId != null && !currentCategories.some((c) => c.formCategoryId === selectedId) && (
          <MUIMenuItem key={`selected-${selectedId}`} value={selectedId} sx={{ display: 'none' }}>
            {selectedName}
          </MUIMenuItem>
        )}

        {parentHistory.length > 1 && (
          <MUIMenuItem
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleBack()
            }}
            sx={{
              margin: '6px 6px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            <ExpandMoreIcon
              sx={{
                color: theme.palette.grey[500],
                mr: 1,
                transform: 'rotate(90deg)',
                width: '12px',
                height: '12px'
              }}
            />
            <MUITypography
              sx={{
                textTransform: 'uppercase',
                mb: 0,
                ml: 1,
                fontSize: '0.75rem',
                pointerEvents: 'none',
                color: 'text.secondary'
              }}
            >
              Back
            </MUITypography>
          </MUIMenuItem>
        )}

        {currentCategories.length > 0 ? (
          currentCategories.map((category: FormEvoCategory) => {
            const hasChildren = (category.formCategories?.length ?? 0) > 0
            return (
              <MUIMenuItem
                key={category.formCategoryId}
                value={category.formCategoryId}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleCategoryClick(category)
                }}
                sx={{
                  margin: '6px 6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  alignItems: 'center',
                  // reserve room so wrapped text doesn't run under the chevron on items with children
                  pr: hasChildren ? '1.75rem' : undefined
                }}
              >
                <FolderIcon sx={{ marginRight: '8px', flexShrink: 0, mt: '2px' }} />
                {category.name}
                {hasChildren && (
                  <ExpandMoreIcon
                    sx={{
                      color: theme.palette.grey[500],
                      mr: 1,
                      width: '12px',
                      height: '12px',
                      position: 'absolute',
                      right: '.50rem',
                      top: '50%',
                      transform: 'translateY(-50%) rotate(270deg)'
                    }}
                  />
                )}
              </MUIMenuItem>
            )
          })
        ) : (
          <MUIMenuItem
            key={'no-categories'}
            value={'' as any}
            disabled
            sx={{
              margin: '6px 6px',
              borderRadius: '4px'
            }}
          >
            No categories found
          </MUIMenuItem>
        )}
      </MUISelect>
    </MUIFormControl>
  )
}
